import { SpeciesData } from '../../data-models/catch-table-models/species-data.model';

/* One field-level difference between a report as DFO received it and as the fisher edited it.
   Either old/new (a plain field) or rows (a catch table) is populated, never both. */
export interface FieldChange {
  field: string; // property name on the report model, kept for reference
  label: string; // human-readable name shown in the notification email
  old?: string;
  new?: string;
  rows?: CatchRowChange[];
}

// a species row that was added to, removed from, or altered within a catch table
export interface CatchRowChange {
  key: string; // species name
  status: 'added' | 'removed' | 'modified';
  changes: ColumnChange[];
}

export interface ColumnChange {
  column: string;
  old: string;
  new: string;
}

// a catch table column to compare - kept/released for daily catch, pieces/weight for offload
export interface CatchColumn {
  key: keyof SpeciesData;
  label: string;
}

/* Builds the list of changes emailed to the helpdesk when a fisher edits an already-submitted
   report. Values are turned into display strings here, where the domain models know how to
   describe themselves, so the email only has to lay them out. Dates are formatted without a
   locale on purpose - the helpdesk reads the notification in English regardless of the locale
   the fisher is running the app in. */
export class ReportDiffService {

  // columns compared on a Daily Catch Report's catch table
  static readonly dailyCatchColumns: CatchColumn[] = [
    { key: 'kept', label: 'Kept' },
    { key: 'released', label: 'Released' },
    { key: 'sizeCode', label: 'Size Code' }
  ];

  // columns compared on an Offload Report's catch table
  static readonly offloadColumns: CatchColumn[] = [
    { key: 'offloadPieces', label: 'Pieces' },
    { key: 'offloadWeight', label: 'Weight' },
    { key: 'sizeCode', label: 'Size Code' }
  ];

  private static readonly months: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];


  /* Adds an entry when the two values differ once formatted for the email. Comparing the
     formatted values (rather than the raw ones) keeps reference-only differences - a rebuilt
     Management Area object holding identical data, a date carrying a different time of day -
     out of the notification. */
  static add(changes: FieldChange[], field: string, label: string, oldValue: any, newValue: any,
             format: (value: any) => string = ReportDiffService.display): void {

    const oldText: string = format(oldValue);
    const newText: string = format(newValue);
    if (oldText === newText) return;

    changes.push({ field, label, old: oldText, new: newText });

  }


  // adds a catch table entry listing only the species rows that actually changed
  static addCatchTable(changes: FieldChange[], field: string, label: string, original: SpeciesData[],
                       updated: SpeciesData[], columns: CatchColumn[]): void {

    const rows: CatchRowChange[] = ReportDiffService.catchRows(original ?? [], updated ?? [], columns);
    if (!rows.length) return; // species reordered but otherwise untouched, nothing worth reporting

    changes.push({ field, label, rows });

  }


  /* Formats a value the way a reader would expect to see it. The area/site/logbook models all
     override toString(), so they describe themselves ('Area 12', '12-3', 'Gill Net (#TX52001)');
     anything without its own toString() falls back to JSON so nothing is silently lost. */
  static display(value: any): string {

    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value instanceof Date) return ReportDiffService.dateTime(value);
    if (Array.isArray(value)) return value.map((item: any) => ReportDiffService.display(item)).filter((item: string) => !!item).join(', ');
    if (typeof value === 'object') return value.toString === Object.prototype.toString ? JSON.stringify(value) : String(value);

    return String(value);

  }


  // date only, for fields DFO receives without a time (date fished, first/last date fished)
  static date(value: any): string {

    const date: Date | null = ReportDiffService.toDate(value);
    if (!date) return '';
    return `${ReportDiffService.months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;

  }


  // date and 24hr time, for fields where the time is part of what DFO receives
  static dateTime(value: any): string {

    const date: Date | null = ReportDiffService.toDate(value);
    if (!date) return '';
    const hours: string = String(date.getHours()).padStart(2, '0');
    const minutes: string = String(date.getMinutes()).padStart(2, '0');
    return `${ReportDiffService.date(date)}, ${hours}:${minutes}`;

  }


  // dates arrive as Date objects from the models but as strings from a stored trip, accept both
  private static toDate(value: any): Date | null {

    if (!value) return null;
    const date: Date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;

  }


  /* Matches catch rows by species so the email shows what changed about a species rather than
     the whole table. Rows the fisher zeroed out are dropped by determineSpeciesData() before we
     get here, so they show up as removals. */
  private static catchRows(original: SpeciesData[], updated: SpeciesData[], columns: CatchColumn[]): CatchRowChange[] {

    const rows: CatchRowChange[] = [];
    const originalRows: Map<string, SpeciesData> = ReportDiffService.keyRows(original);
    const updatedRows: Map<string, SpeciesData> = ReportDiffService.keyRows(updated);

    updatedRows.forEach((species: SpeciesData, key: string) => {

      const before: SpeciesData | undefined = originalRows.get(key);
      const changes: ColumnChange[] = columns
        .map((column: CatchColumn) => ({
          column: column.label,
          old: before ? ReportDiffService.display(before[column.key]) : '',
          new: ReportDiffService.display(species[column.key])
        }))
        .filter((change: ColumnChange) => change.old !== change.new);

      if (!changes.length) return;
      rows.push({ key, status: before ? 'modified' : 'added', changes });

    });

    originalRows.forEach((species: SpeciesData, key: string) => {

      if (updatedRows.has(key)) return;
      const changes: ColumnChange[] = columns
        .map((column: CatchColumn) => ({ column: column.label, old: ReportDiffService.display(species[column.key]), new: '' }))
        .filter((change: ColumnChange) => change.old !== change.new);
      rows.push({ key, status: 'removed', changes });

    });

    return rows;

  }


  private static keyRows(speciesDataList: SpeciesData[]): Map<string, SpeciesData> {

    const rows: Map<string, SpeciesData> = new Map<string, SpeciesData>();
    speciesDataList.forEach((species: SpeciesData) => rows.set(species.speciesName || species.speciesCode || 'Unnamed species', species));
    return rows;

  }

}
